'use strict';

import {
  CubeCamera,
  DoubleSide,
  Mesh,
  OrthographicCamera,
  PlaneGeometry,
  RawShaderMaterial, WebGLCubeRenderTarget,
  WebGLRenderTarget,
} from 'three';

const vertexShader = `
attribute vec3 position;
attribute vec2 uv;

uniform mat4 projectionMatrix;
uniform mat4 modelViewMatrix;

varying vec2 vUv;

void main()  {

	vUv = vec2( 1.- uv.x, uv.y );
	gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 );

}
`;

const fragmentShader = `
precision mediump float;

uniform samplerCube map;

varying vec2 vUv;

#define M_PI 3.1415926535897932384626433832795

void main()  {

	vec2 uv = vUv;

	float longitude = uv.x * 2. * M_PI - M_PI + M_PI / 2.;
	float latitude = uv.y * M_PI;

	vec3 dir = vec3(
		- sin( longitude ) * sin( latitude ),
		cos( latitude ),
		- cos( longitude ) * sin( latitude )
	);
	normalize( dir );

	gl_FragColor = textureCube( map, dir );

}
`;


export class CubemapToEquirectangular {

  constructor (renderer,size,provideCubeCamera,flip = false) {
    this.width = 1;
    this.height = 1;
    this.flip = flip;
    this.renderer = renderer;

    this.material = new RawShaderMaterial( {
      uniforms: {
        map: { type: 't', value: null }
      },
      vertexShader: vertexShader,
      fragmentShader: fragmentShader,
      side: DoubleSide,
      transparent: true
    } );

    this.quad = new Mesh(
      new PlaneGeometry( 1, 1 ),
      this.material
    );
    this.camera = new OrthographicCamera( 1 / - 2, 1 / 2, 1 / 2, 1 / - 2, -10000, 10000 );

    this.canvas = document.createElement( 'canvas' );
    this.ctx = this.canvas.getContext( '2d' );
    this.cubeCamera = null;

    this.setSize( size, size / 2 );

    const gl = this.renderer.getContext();
    this.cubeMapSize = gl.getParameter( gl.MAX_CUBE_MAP_TEXTURE_SIZE );

    provideCubeCamera && this.getCubeCamera(size);
  }

  update(camera,scene) {
    const autoClear = this.renderer.autoClear;
    this.renderer.autoClear = true;
    this.cubeCamera.position.copy( camera.position );
    this.cubeCamera.update( this.renderer, scene );
    this.renderer.autoClear = autoClear;

    this.convert( this.cubeCamera );
  }

  getCubeCamera(size) {
    const cubeMapSize = Math.min( this.cubeMapSize, size );
    this.cubeCamera = new CubeCamera( .1, 10000, new WebGLCubeRenderTarget( cubeMapSize, cubeMapSize ));
    return this.cubeCamera;
  }

  setSize(width, height) {
    this.width = width;
    this.height = height;

    this.quad.scale.set( this.flip ? -this.width : this.width, this.height, 1 );

    this.camera.left = this.width / - 2;
    this.camera.right = this.width / 2;
    this.camera.top = this.height / 2;
    this.camera.bottom = this.height / - 2;

    this.camera.updateProjectionMatrix();
    this.canvas.width = this.width;
    this.canvas.height = this.height;
  }

  convert(cubeCamera,download = true) {
    this.material.uniforms.map.value = cubeCamera.renderTarget.texture;
    const rt = new WebGLRenderTarget(this.width,this.height);
    this.renderer.setRenderTarget(rt);
    this.renderer.render( this.quad, this.camera);
    this.renderer.setRenderTarget(null);

    const pixels = new Uint8Array( 4 * this.width * this.height );
    this.renderer.readRenderTargetPixels( rt, 0, 0, this.width, this.height,pixels);
    const imageData = new ImageData( new Uint8ClampedArray( pixels ), this.width, this.height );

    download && this.download( imageData );

    return imageData;
  }

  download(imageData) {
    this.ctx.putImageData( imageData, 0, 0 );

    this.canvas.toBlob( function( blob ) {

      const url = URL.createObjectURL(blob);
      const fileName = 'pano-' + document.title + '-' + Date.now() + '.png';
      const anchor = document.createElement( 'a' );
      anchor.href = url;
      anchor.setAttribute("download", fileName);
      anchor.innerHTML = "downloading...";
      anchor.style.display = "none";
      document.body.appendChild(anchor);
      setTimeout(function() {
        anchor.click();
        document.body.removeChild(anchor);
      }, 1 );

    }, 'image/png' );
  }

}
